import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, List, Typography } from '@/primer/components';
import type { ListItemProps } from '@/primer/components';
import { useAuth } from '@/api';
import { PageLayout } from '@/components/layout';

// ─── Section label ─────────────────────────────────────────────────────────────

function SectionLabel({ children, testId }: { children: React.ReactNode; testId: string }) {
  return (
    <Typography
      variant="sectionHeader"
      size="medium"
      className="uppercase tracking-widest px-1 mb-2"
      data-testid={testId}
    >
      {children}
    </Typography>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Settings hub screen.
 *
 * A navigation list with three sections — Account, Preferences, Privacy and Data —
 * plus a log-out button. Each working item navigates to its own sub-screen.
 * Stub items (Notifications, App language, data/privacy items) render without
 * an onClick so they are non-interactive until the screens are built.
 */
export function Settings() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate({ to: '/login' });
    } catch (err) {
      console.error('Failed to logout:', err);
      setIsLoggingOut(false);
    }
  };

  // ── ACCOUNT ──────────────────────────────────────────────────────────────

  const accountItems: ListItemProps[] = [
    {
      title: 'Profile',
      icon: 'user',
      trailingIcon: 'chevron-right',
      onClick: () => navigate({ to: '/settings/profile' }),
    },
    {
      title: 'Connect socials',
      icon: 'instagram',
      trailingIcon: 'chevron-right',
      onClick: () => navigate({ to: '/settings/connect' }),
    },
    {
      title: 'Shipping address',
      icon: 'truck',
      trailingIcon: 'chevron-right',
      onClick: () => navigate({ to: '/settings/address' }),
    },
    {
      title: 'Password and email',
      icon: 'lock',
      trailingIcon: 'chevron-right',
      onClick: () => navigate({ to: '/settings/email' }),
    },
  ];

  // ── PREFERENCES ──────────────────────────────────────────────────────────

  const preferenceItems: ListItemProps[] = [
    {
      title: 'Notifications',
      icon: 'bell',
      trailingIcon: 'chevron-right',
    },
    {
      title: 'App language',
      icon: 'speech-bubble-language',
      trailingIcon: 'chevron-right',
    },
  ];

  // ── PRIVACY AND DATA ──────────────────────────────────────────────────────

  const privacyItems: ListItemProps[] = [
    {
      title: 'Data consent',
      icon: 'id-card',
      trailingIcon: 'chevron-right',
    },
    {
      title: 'Data request',
      icon: 'send',
      trailingIcon: 'chevron-right',
    },
    {
      title: 'Delete account',
      icon: 'trash',
      trailingIcon: 'chevron-right',
    },
    {
      title: 'CCPA compliance',
      icon: 'document',
      trailingIcon: 'external-link',
    },
    {
      title: 'Terms of service',
      icon: 'document',
      trailingIcon: 'external-link',
    },
    {
      title: 'Privacy policy',
      icon: 'document',
      trailingIcon: 'external-link',
    },
  ];

  return (
    <PageLayout className="py-7">
      <div className="flex flex-col gap-6">
        {/* ACCOUNT */}
        <section>
          <SectionLabel testId="section-account">Account</SectionLabel>
          <List items={accountItems} />
        </section>

        {/* PREFERENCES */}
        <section>
          <SectionLabel testId="section-preferences">Preferences</SectionLabel>
          <List items={preferenceItems} />
        </section>

        {/* PRIVACY AND DATA */}
        <section>
          <SectionLabel testId="section-privacy">Privacy and Data</SectionLabel>
          <List items={privacyItems} />
        </section>

        {/* LOG OUT */}
        <Button fullWidth onClick={handleLogout} isLoading={isLoggingOut}>
          Log out
        </Button>
      </div>
    </PageLayout>
  );
}

export default Settings;
